import React from 'react';
import { IconButton } from 'subframe/components/IconButton';
import { TextArea } from 'subframe/components/TextArea';
import { Button } from 'subframe/components/Button';
import { Dialog } from 'subframe/components/Dialog';
import { Accordion } from 'subframe/components/Accordion';
import { CopyToClipboard } from 'src/components/design-system/CopyToClipboard';
import { CodeBlock } from 'subframe/components/CodeBlock';
import useUserAccountState from 'hooks/useUserAccountState';
import { useSnackbar } from 'notistack';
import AnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import FormProvider from 'components/FormProvider';
import { SetStateFunction } from 'models/react';
import { submitFeedback } from 'api/frontend';
import { SubmitFeedbackRequest } from 'api/models';
import { somethingWentWrong, toastAutoHideDuration } from 'constants/toasts';
import * as Sentry from '@sentry/browser';
import { fieldIsRequiredErrorMessage } from 'constants/input-validation';

interface CustomMappingsFields {
  query: string;
}

interface CustomMappingsDialogProps {
  open: boolean;
  handleOpenChange: SetStateFunction<boolean>;
}

type ActiveAccordion = 'example' | 'instructions' | null;

export const UploadCustomMappingsDialog = (
  props: CustomMappingsDialogProps,
) => {
  const { open, handleOpenChange } = props;
  const { account, user } = useUserAccountState();
  const { enqueueSnackbar } = useSnackbar();
  const { logEvent } = AnalyticsEventLogger();

  const SubmissionError = somethingWentWrong.replace(
    '<action>',
    'submitting custom mappings',
  );

  const MappingsSchema = Yup.object().shape({
    query: Yup.string()
      .required(fieldIsRequiredErrorMessage('JSON mappings'))
      .test('is-json', 'Must be valid JSON', (value) => {
        try {
          if (value) JSON.parse(value);
          return true;
        } catch {
          return false;
        }
      }),
  });

  const defaultValues: CustomMappingsFields = {
    query: '',
  };

  const methods = useForm<CustomMappingsFields>({
    resolver: yupResolver(MappingsSchema),
    defaultValues: defaultValues,
  });

  const [activeAccordion, setActiveAccordion] =
    React.useState<ActiveAccordion>(null);

  React.useEffect(() => {
    if (!open) {
      methods.reset(defaultValues);
    }
  }, [open]);

  const {
    handleSubmit,
    formState: { isSubmitting, errors },
  } = methods;

  const onSubmit = async (data: CustomMappingsFields) => {
    try {
      const reqBody: SubmitFeedbackRequest = {
        title: 'customMappings',
        details: data.query,
        user_email: user?.email || '',
      };
      await submitFeedback(reqBody, {
        headers: {
          Authorization: `Bearer ${account.token}`,
        },
      });
      logEvent('custom-mappings-submitted');
      enqueueSnackbar('Custom mappings submitted successfully', {
        variant: 'success',
      });
      handleOpenChange(false);
    } catch (error: any) {
      Sentry.captureException(error);

      // Handle 400 Bad Request specifically
      if (error.response?.status === 400) {
        const errorMessage =
          error.response.data?.message ||
          error.response.data ||
          'Invalid request format';
        enqueueSnackbar(`Submission failed: ${errorMessage}`, {
          variant: 'error',
          autoHideDuration: toastAutoHideDuration,
        });
      } else {
        // Handle other errors with generic message
        enqueueSnackbar(SubmissionError, {
          variant: 'error',
          autoHideDuration: toastAutoHideDuration,
        });
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <div className="flex w-144 grow shrink-0 basis-0 flex-col items-center justify-end gap-4 px-4 py-4">
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-default-font">
                Upload Custom Mappings
              </span>
              <IconButton
                icon="FeatherX"
                size="medium"
                onClick={() => handleOpenChange(false)}
              />
            </div>

            <TextArea
              className="h-auto grow shrink-0 basis-0"
              label="Provide the JSON defining how your internal image references map to upstream containers."
              helpText={errors.query?.message}
              error={methods.getFieldState('query').invalid}
            >
              <TextArea.Input
                className="h-80 w-full flex-none"
                {...methods.register('query')}
              />
            </TextArea>

            <div className="flex w-full items-end justify-center gap-4">
              <Button
                variant="brand-primary"
                size="large"
                icon={null}
                loading={isSubmitting}
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting}
              >
                Submit
              </Button>
            </div>

            <div className="flex w-full flex-col items-center justify-center">
              <Accordion
                open={activeAccordion === 'example'}
                onOpenChange={(isOpen) => {
                  setActiveAccordion(isOpen ? 'example' : null);
                }}
                trigger={
                  <div className="flex w-full items-center gap-2 pr-3 py-2">
                    <span className="grow shrink-0 basis-0 text-label-bold font-label-bold text-default-font">
                      Click here to see an example JSON
                    </span>
                    <Accordion.Chevron />
                  </div>
                }
              >
                <CopyToClipboard
                  className="w-full grow shrink-0 basis-0"
                  text={
                    '{\n  "image_mappings": [\n    {\n      "addon_name": "KEDA",\n      "internal_tag": "mycorp-registry.example.com/keda-fork:special-build",\n      "upstream_reference": "docker.io/kedacore/keda:v2.14.1",\n      "version_mapping": {\n        "internal_format": "acme-v2.4.{patch}",\n        "upstream_format": "v2.14.{patch}"\n      }\n    }\n  ]\n}'
                  }
                />
              </Accordion>

              <Accordion
                open={activeAccordion === 'instructions'}
                onOpenChange={(isOpen) => {
                  setActiveAccordion(isOpen ? 'instructions' : null);
                }}
                trigger={
                  <div className="flex w-full items-center gap-2 pr-3 py-2">
                    <span className="grow shrink-0 basis-0 text-label-bold font-label-bold text-default-font">
                      Learn how to program custom mappings via Kubernetes labels
                    </span>
                    <Accordion.Chevron />
                  </div>
                }
              >
                <div className="flex w-full flex-col items-start">
                  <span className="text-body font-body text-default-font">
                    Add a label to your Kubernetes manifest with a short key
                    referencing the upstream image, for example:{' '}
                  </span>
                  <CodeBlock className="h-auto w-full flex-none">
                    {[
                      'yaml',
                      ' metadata:',
                      '   labels:',
                      '     upstream_image_ref: istio/pilot:1.17.3',
                    ].join('\n')}
                  </CodeBlock>
                  <span className="text-body font-body text-default-font">
                    Chkk will classify the images based on the
                    upstream_image_ref labels.
                  </span>
                </div>
              </Accordion>
            </div>
          </div>
        </FormProvider>
      </Dialog.Content>
    </Dialog>
  );
};
